import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Wand2, X, Loader2, CheckCircle, AlertCircle, Play, Download, Upload, Link2, Image, Palette, Settings, Eye } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, inputNeed } from '../../utils/nodeRequirements';

const MODEL_OPTIONS = [
  { id: 'fast', name: 'Rápido', description: 'Mejor velocidad' },
  { id: 'animated', name: 'Animado', description: 'Estilo cartoon' },
  { id: 'realistic', name: 'Realista', description: 'Mayor detalle' },
];

const StyleTransferNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [styleImageLocal, setStyleImageLocal] = useState(null);
  const [structureImageLocal, setStructureImageLocal] = useState(null);
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [model, setModel] = useState(data.model || 'fast');
  const [strength, setStrength] = useState(data.strength ?? 0.65);
  
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);
  
  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';
  const { openImagePreview } = useMediaPreview();

  // Check for connected inputs
  const styleEdge = edges.find((e) => e.target === id && e.targetHandle === 'inputStyle');
  const structureEdge = edges.find((e) => e.target === id && e.targetHandle === 'inputStructure');
  
  // Get connected node data
  const getConnectedNodeData = (edge) => {
    if (!edge) return null;
    const sourceNode = nodes.find((n) => n.id === edge.source);
    return sourceNode?.data;
  };
  
  const styleInputData = getConnectedNodeData(styleEdge);
  const structureInputData = getConnectedNodeData(structureEdge);
  
  const handleSourcePickStyle = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        const reader = new FileReader();
        reader.onload = (e) => setStyleImageLocal({ file: result.file, preview: e.target.result, name: result.file.name });
        reader.readAsDataURL(result.file);
      } else if (result.url) {
        setStyleImageLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, openSourcePicker]);

  const handleSourcePickStructure = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        const reader = new FileReader();
        reader.onload = (e) => setStructureImageLocal({ file: result.file, preview: e.target.result, name: result.file.name });
        reader.readAsDataURL(result.file);
      } else if (result.url) {
        setStructureImageLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, openSourcePicker]);
  
  // Determine which files to use
  const styleSource = styleInputData?.hasOutput ? { 
    file: styleInputData.file, 
    preview: styleInputData.outputUrl || styleInputData.preview,
    name: styleInputData.fileName || 'style_connected',
    fromConnection: true
  } : styleImageLocal;
  
  const structureSource = structureInputData?.hasOutput ? { 
    file: structureInputData.file, 
    preview: structureInputData.outputUrl || structureInputData.preview,
    name: structureInputData.fileName || 'structure_connected',
    fromConnection: true
  } : structureImageLocal;

  // Chronometer effect
  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => {
        setChronometer((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStyleImageChange = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setStyleImageLocal({ file, preview: e.target.result, name: file.name });
    };
    reader.readAsDataURL(file);
  };

  const handleStructureImageChange = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setStructureImageLocal({ file, preview: e.target.result, name: file.name });
    };
    reader.readAsDataURL(file);
  };

  const fetchFileFromUrl = async (url, filename) => blobToFile(url, filename, 'image/jpeg');

  const handleProcess = useCallback(async () => {
    let styleFile = styleSource?.file;
    let structureFile = structureSource?.file;
    const hasStructure = !!(structureSource?.file || structureSource?.preview);
    const directParams = {
      prompt,
      model,
      output_format: 'png',
      ...(hasStructure
        ? { structure_depth_strength: 1, structure_denoising_strength: strength }
        : {}),
    };
    const files = [];

    if (!styleFile && styleSource?.preview) {
      if (styleSource.preview.startsWith('http')) {
        directParams.style_image_url = styleSource.preview;
      } else {
        styleFile = await fetchFileFromUrl(styleSource.preview, styleSource.name);
      }
    }
    if (styleFile) files.push({ key: 'style_image_url', file: styleFile });

    if (!structureFile && structureSource?.preview) {
      if (structureSource.preview.startsWith('http')) {
        directParams.structure_image_url = structureSource.preview;
      } else {
        structureFile = await fetchFileFromUrl(structureSource.preview, structureSource.name);
      }
    }
    if (structureFile) files.push({ key: 'structure_image_url', file: structureFile });

    if (!styleFile && !directParams.style_image_url) return;
    resetExec();

    const result = await processNode({
      resultType: 'style_transfer',
      files,
      params: directParams,
    });

    if (result?.outputUrl) {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'image',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'image',
        tool: 'styleTransfer',
        filename: `style_${styleSource?.name || 'transfer'}`,
        url: result.outputUrl,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, styleSource, structureSource, prompt, model, strength, updateNodeData, addAsset, processNode, resetExec]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const hasValidStyle = styleSource?.preview || styleSource?.file || (styleInputData?.hasOutput);

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([inputNeed('style', !!hasValidStyle, { connected: !!styleEdge })]);

  return (
    <div className={`node-container style-transfer-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="style-transfer-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="styletransfer-reconfigure-notice"
        />
      )}
      {/* Input Handles */}
      <Handle
        type="target"
        position={Position.Left}
        id="inputStyle"
        className="node-handle input-handle style-handle-style"
        style={{ top: '25%' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="inputStructure"
        className="node-handle input-handle style-handle-structure"
        style={{ top: '50%' }}
      />
      
      {/* Header */}
      <div className="node-header style-transfer-header">
        <div className="node-header-left">
          <Wand2 size={16} />
          <span>Style Transfer</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content style-transfer-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>Transfiriendo estilo...</span>
            </div>
            <p className="node-processing-hint">Modelo: {model}</p>
          </div>
        ) : !showForm ? (
          <div className="node-result">
            <img 
              src={data.outputUrl} 
              alt="Result" 
              className="node-result-image clickable"
              onClick={() => openImagePreview(data.outputUrl, 'Estilo Transferido')}
            />
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="styletransfer-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="styletransfer-reconfigure-btn" />
              <button 
                className="node-mini-preview-btn"
                onClick={() => openImagePreview(data.outputUrl, 'Estilo Transferido')}
              >
                <Eye size={12} />
                Ver
              </button>
              <button 
                className="node-mini-download-btn"
                onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `styled_${Date.now()}.png`); }}
              >
                <Download size={12} />
                Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="image" label="styletransfer" testIdPrefix="styletransfer-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Estilo transferido</span>
            </div>
          </div>
        ) : (
          <>
            {/* Input Images Grid */}
            <div className="style-inputs-grid">
              {/* Style Image */}
              <div className="style-input-slot">
                <span className="style-input-label">
                  <Palette size={10} />
                  Estilo *
                  {styleEdge && <Link2 size={10} className="connected-icon" />}
                </span>
                {styleSource?.preview ? (
                  <div className="style-preview-box">
                    <img src={styleSource.preview} alt="Style" />
                    {!styleSource.fromConnection && (
                      <button className="style-clear-btn" onClick={() => setStyleImageLocal(null)}>
                        <X size={10} />
                      </button>
                    )}
                    {styleSource.fromConnection && (
                      <div className="style-connected-badge"><Link2 size={8} /></div>
                    )}
                  </div>
                ) : styleEdge ? (
                  <div className="style-waiting-input">
                    <Link2 size={14} />
                    <span>Esperando...</span>
                  </div>
                ) : (
                  <button type="button" className="node-load-btn" onClick={handleSourcePickStyle} data-testid="load-btn-style">
                    <Upload size={14} /> Cargar
                  </button>
                )}
              </div>

              {/* Structure Image (optional) */}
              <div className="style-input-slot">
                <span className="style-input-label">
                  <Image size={10} />
                  Estructura
                  {structureEdge && <Link2 size={10} className="connected-icon" />}
                </span>
                {structureSource?.preview ? (
                  <div className="style-preview-box">
                    <img src={structureSource.preview} alt="Structure" />
                    {!structureSource.fromConnection && (
                      <button className="style-clear-btn" onClick={() => setStructureImageLocal(null)}>
                        <X size={10} />
                      </button>
                    )}
                    {structureSource.fromConnection && (
                      <div className="style-connected-badge"><Link2 size={8} /></div>
                    )}
                  </div>
                ) : structureEdge ? (
                  <div className="style-waiting-input">
                    <Link2 size={14} />
                    <span>Esperando...</span>
                  </div>
                ) : (
                  <button type="button" className="node-load-btn" onClick={handleSourcePickStructure} data-testid="load-btn-structure">
                    <Upload size={14} /> Opcional
                  </button>
                )}
              </div>
            </div>

            {/* Model Selector */}
            <div className="style-setting">
              <label><Settings size={10} /> Modelo</label>
              <div className="style-model-options">
                {MODEL_OPTIONS.map((m) => (
                  <button
                    key={m.id}
                    className={`style-model-btn ${model === m.id ? 'active' : ''}`}
                    onClick={() => { setModel(m.id); updateNodeData(id, { model: m.id }); }}
                    data-testid={`style-model-${m.id}`}
                    title={m.description}
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Strength slider (only if structure image) */}
            {(structureSource || structureEdge) && (
              <div className="style-setting">
                <label>Fuerza del estilo: {Math.round(strength * 100)}%</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={strength}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setStrength(v);
                    updateNodeData(id, { strength: v });
                  }}
                  className="style-slider"
                  data-testid="style-strength-slider"
                />
              </div>
            )}

            {/* Prompt */}
            <div className="style-prompt-section">
              <PromptField
                value={prompt}
                onChange={(text) => { setPrompt(text); updateNodeData(id, { prompt: text }); }}
                nodeId={id}
                nodeType="styleTransfer"
                label="Prompt del estilo"
                placeholder="Prompt opcional (ej: cinematic, detailed)"
                className="style-prompt-input"
                rows={2}
                testId="style-prompt-input"
              />
            </div>

            {/* Process Button */}
            <button
              className="node-process-btn style-process-btn"
              onClick={handleProcess}
              disabled={!hasValidStyle}
              title={requirementsTitle(runMissing)}
              data-testid="style-transfer-generate-btn"
            >
              <Wand2 size={14} />
              Transferir Estilo
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>
      
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

StyleTransferNode.displayName = 'StyleTransferNode';

export default StyleTransferNode;
